import { WritableComputedRef } from "vue";
import { useProspectStore } from "~/store/prospect-store";
import { EntityTypeEnum } from "~~/enums/EntityType";
import { ProspectPageEnum } from "~~/enums/ProspectPage";
import CurrentPage from "~~/models/CurrentPage";
import Discussions from "~~/models/Discussions";
import MessagesStatusModel from "~~/models/MessagesStatusModel";
import { useGtag } from "vue-gtag-next";

export const useProspect = () => {
  let prospectStore = useProspectStore();
  let helper = useHelper();
  const localePath = useLocalePath();
  const gtag = useGtag();

  const getProspect = async function (prospectId: string) {
    await prospectStore.loadProspect(prospectId);
    return prospectStore.getProspect;
  };

  const createProspect = async function (accountType: string, prospect: any) {
    if (accountType === "individual") {
      await prospectStore.addIndividual(prospect);
    } else {
      await prospectStore.addCorporate(prospect);
    }

    // Send Google Analytic event
    gtag.event("event", {
      event_category: "Account Opening",
      event_label: "Submit MyCIM Form " + prospect.languageIsoCode,
    });
  };

  const editProspect = function (prospectId: string) {
    navigateTo(localePath(`/prospect/${prospectId}`) + "/");
  };

  const getProspectList = async function (): Promise<
    ComputedRef<any[] | null>
  > {
    await prospectStore.loadProspectList();

    return computed(() => prospectStore.getProspectList);
  };

  const getProspectType = function (): EntityTypeEnum {
    return prospectStore.getProspectType;
  };

  const setProspectType = function (entityType: EntityTypeEnum) {
    return prospectStore.setProspectType(entityType);
  };

  const addAttachment = async function (
    file: File,
    successCallback: Function,
    failCallback: Function
  ) {
    try {
      await prospectStore.addAttachment(file);
      await successCallback(prospectStore.getAttachmentId);
    } catch (error) {
      await failCallback(error);
    }
  };

  const downloadAttachment = async function (
    attachmentId: string,
    fileName: string
  ) {
    await prospectStore.loadAttachment(attachmentId);
    let file = prospectStore.getAttachment;
    if (file) {
      helper.downloadFile(file, fileName, true);
    }
  };

  const getDiscussions = async function (
    prospectId: string
  ): Promise<WritableComputedRef<Discussions | null>> {
    await prospectStore.loadDiscussions(prospectId);
    return computed<Discussions | null>({
      get: () => prospectStore.getProspectDiscussions,
      set: (value: Discussions | null) => (prospectStore.discussions = value),
    });
  };

  const getDiscussion = async function (
    discussionId?: string,
    prospectId?: string
  ) {
    await prospectStore.loadDiscussion(discussionId, prospectId);
    return prospectStore.getProspectDiscussion;
  };

  const addMessage = async function (
    prospectId: string,
    message: string,
    attachmentIds: Array<string>
  ): Promise<boolean> {
    return await prospectStore.addMessage(prospectId, message, attachmentIds);
  };

  const updateMessageStatus = async function (
    messageIds: Array<string>
  ): Promise<MessagesStatusModel> {
    return await prospectStore.updateMessageStatus(messageIds);
  };

  const getProspectCurrentPage = async function (
    prospectId: any
  ): Promise<CurrentPage> {
    return await prospectStore.getProspectCurrentPage(prospectId);
  };

  const getPageAccessForProspectId = async function (
    prospectId: any,
    currentPage: ProspectPageEnum
  ): Promise<boolean> {
    const hasAccess = await prospectStore.getPageAccessForProspectId(
      prospectId,
      currentPage
    );
    return hasAccess;
  };

  // region Register Individual
  const saveIndividualLandingPage = async function (
    accountUsage: any,
    prospectId: any
  ) {
    const payload = {
      prospectId: prospectId,
      accountUsage: accountUsage,
    };
    const response = await prospectStore.saveIndividualLandingPage(payload);
    return response;
  };

  const saveIndividualRegistration = async function (
    prospectHolder: any,
    prospectId: any
  ) {
    const payload = {
      prospectId: prospectId,
      addressLine1: prospectHolder.mainAddress?.line1,
      zipCode: prospectHolder.mainAddress?.zipCode,
      city: prospectHolder.mainAddress?.city,
      addressLine2: prospectHolder.mainAddress?.line2,
      residenceCountryIsoCode: prospectHolder.mainAddress?.countryIsoCode,
      passportNumber: prospectHolder.identityDocuments[0].documentNumber,
      passportExpirationDate: prospectHolder.identityDocuments[0].expirationDate
        ? useHelper().convertDateToTechnicalString(
            new Date(prospectHolder.identityDocuments[0].expirationDate)
          )
        : null,
      taxationCountryIsoCode:
        prospectHolder.taxations?.length > 0
          ? prospectHolder.taxations[0].taxationCountryIsoCode
          : null,
      tinNumber:
        prospectHolder.taxations?.length > 0
          ? prospectHolder.taxations[0].tinNumber
          : null,
      hasTinNumber:
        prospectHolder.taxations?.length > 0
          ? prospectHolder.taxations[0].hasTinNumber
          : true,
      isLinkedToUnitedStates: !!prospectHolder.isLinkedToUnitedStates,
      hasUSPassportOrGreenCard: prospectHolder.hasUSPassportOrGreenCard,
      hasSpentSubstantialTimeInUS: prospectHolder.hasSpentSubstantialTimeInUS,
      isUSResidentForAnyOtherReason:
        prospectHolder.isUSResidentForAnyOtherReason,
    };

    const response = await prospectStore.saveIndividualRegistration(payload);
    return response;
  };

  const saveIndividualPreKyc = async function (
    prospectHolder: any,
    prospectId: any
  ) {
    const payload = {
      prospectId: prospectId,
      activityId: prospectHolder.activityId,
      workplaceCountryIsoCode: prospectHolder.workplaceCountryIsoCode,
      maritalStatus: prospectHolder.maritalStatus,
      needJointAccount: !!prospectHolder.needJointAccount,
      partner: prospectHolder.partner,
      children: prospectHolder.children.filter(
        (child) => child.lastName && child.firstName
      ),
    };
    const response = await prospectStore.saveIndividualPreKyc(payload);
    return response;
  };

  const SaveIndividualTermsAndConditions = async function (
    prospectHolder: any,
    prospectId: any
  ) {
    const payload = {
      prospectId: prospectId,
      hasReadAndAgreeWithContract: prospectHolder.hasReadAndAgreeWithContract,
      hasReadAndAgreeWithTermsAndConditions:
        prospectHolder.hasReadAndAgreeWithTermsAndConditions,
      hasReadAndAgreeWithFeesAndCommissions:
        prospectHolder.hasReadAndAgreeWithFeesAndCommissions,
      hasReadAndAgreeWithRisksInvolvedInTradingFinancialInstruments:
        prospectHolder.hasReadAndAgreeWithRisksInvolvedInTradingFinancialInstruments,
    };

    const response = await prospectStore.SaveIndividualTermsAndConditions(
      payload
    );
    return response;
  };

  const saveIndividualToInProgress = async function (prospectId: any) {
    const payload = {
      prospectId: prospectId,
    };
    const response = await prospectStore.saveIndividualToInProgress(payload);
    return response;
  };

  const saveIndividualDocuments = async function (
    certificateOfResidenceId: String,
    passportId: String,
    prospectId: String
  ) {
    const payload = {
      prospectId: prospectId,
      certificateOfResidenceId: certificateOfResidenceId,
      passportId: passportId,
    };

    const response = await prospectStore.saveIndividualDocuments(payload);
    return response;
  };

  const downloadOpeningSet = async function (
    prospectId: string,
    includesDefaults: boolean,
    isReadonly: boolean,
    isEsignature: boolean,
    languageIsoCode: string,
    templateSelectionList?: Array<string>
  ) {
    await prospectStore.downloadOpeningSet(
      prospectId,
      includesDefaults,
      isReadonly,
      isEsignature,
      languageIsoCode,
      templateSelectionList
    );
    let fileData = prospectStore.getOpeningSet;

    if (fileData) {
      helper.downloadFile(fileData, `openingSet_${prospectId}.pdf`, true);
    }
  };

  // endregion

  // region Register Corporate
  const saveCorporateLandingPage = async function (
    accountUsage: any,
    prospectId: any
  ) {
    const payload = {
      prospectId: prospectId,
      accountUsage: accountUsage,
    };
    const response = await prospectStore.saveCorporateLandingPage(payload);
    return response;
  };

  const saveCorporateRegistration = async function (
    prospectHolder: any,
    prospectId: string
  ) {
    const payload = {
      prospectId: prospectId,
      addressLine1: prospectHolder.mainAddress?.line1,
      addressLine2: prospectHolder.mainAddress?.line2,
      zipCode: prospectHolder.mainAddress?.zipCode,
      residenceCountryIsoCode: prospectHolder.mainAddress?.countryIsoCode,
      city: prospectHolder.mainAddress?.city,
      incorporationCountryIsoCode: prospectHolder.incorporationCountryIsoCode,
      activityId: prospectHolder.activityId,
      activityRemark: prospectHolder.activityRemark,
      webSite: prospectHolder.webSite,
      shareCapital: prospectHolder.shareCapital,
      shareCapitalCurrencyIsoCode: prospectHolder.shareCapitalCurrencyIsoCode,
      dateOfIncorporation: prospectHolder.dateOfIncorporation
        ? useHelper().convertDateToTechnicalString(
            new Date(prospectHolder.dateOfIncorporation)
          )
        : null,
      officeCountryIsoCode: prospectHolder.officeCountryIsoCode,
      hasBalanceSheetFinancialStatement:
        prospectHolder.hasBalanceSheetFinancialStatement,
      hasExternalAccounting: prospectHolder.hasExternalAccounting,
      hasPhysicalOffice: prospectHolder.hasPhysicalOffice,
      hasDirectorInPlaceOfIncorporation:
        prospectHolder.hasDirectorInPlaceOfIncorporation,
      holdAnOperatingCompany: prospectHolder.holdAnOperatingCompany,
      hasEmployeesInPlaceOfIncorporation:
        prospectHolder.hasEmployeesInPlaceOfIncorporation,
      numberOfEmployees: prospectHolder.numberOfEmployees,
      hasReadAndAgreeWithFeesAndCommissions:
        prospectHolder.hasReadAndAgreeWithFeesAndCommissions,
      hasReadAndAgreeWithTermsAndConditions:
        prospectHolder.hasReadAndAgreeWithTermsAndConditions,
      hasReadAndAgreeWithRisksInvolvedInTradingFinancialInstruments:
        prospectHolder.hasReadAndAgreeWithRisksInvolvedInTradingFinancialInstruments,
    };

    const response = await prospectStore.saveCorporateRegistration(payload);
    return response;
  };

  const saveCorporateIndividual = async function (
    entity: any,
    prospectId: string
  ) {
    const payload = {
      prospectId: prospectId,
      personId: entity.id,

      addressLine1: entity.mainAddress?.line1,
      addressLine2: entity.mainAddress?.line2,
      zipCode: entity.mainAddress?.zipCode,
      city: entity.mainAddress?.city,
      residenceCountryIsoCode: entity.mainAddress?.countryIsoCode,

      gender: entity.gender,
      firstName: entity.firstName,
      lastName: entity.lastName,
      middleName: entity.middleName,
      email: entity.email,
      mobilePhoneNumber: entity.mobilePhoneNumber?.number,

      nationalityIsoCode: entity.nationalityIsoCode,
      isLinkedToUnitedStates: entity.isLinkedToUnitedStates,
      identityDocuments: entity.identityDocuments.map((d) => {
        var tmp = { ...d };
        tmp.expirationDate = tmp.expirationDate
          ? useHelper().convertDateToTechnicalString(
              new Date(tmp.expirationDate)
            )
          : null;
        return tmp;
      }),

      //passportNumber: entity.passportNumber,
      // passportExpirationDate: entity.passportExpirationDate
      //   ? useHelper().convertDateToTechnicalString(
      //       new Date(entity.passportExpirationDate)
      //     )
      //   : null,
      birthDate: entity.birthDate
        ? useHelper().convertDateToTechnicalString(new Date(entity.birthDate))
        : null,
      cityOfBirth: entity.cityOfBirth,
      countryOfBirthIsoCode: entity.countryOfBirthIsoCode,

      signatureType: entity.signatureType,
      role: entity.role,
      percentageOfOwnership: entity.percentageOfOwnership,
      activityId: entity.activityId,
      workplaceCountryIsoCode: entity.workplaceCountryIsoCode,
      hasSpentSubstantialTimeInUS: entity.hasSpentSubstantialTimeInUS,
      hasUSPassportOrGreenCard: entity.hasUSPassportOrGreenCard,
      taxationCountryIsoCode:
        entity.taxations?.length > 0
          ? entity.taxations[0].taxationCountryIsoCode
          : null,
      hasTinNumber:
        entity.taxations?.length > 0 ? entity.taxations[0].hasTinNumber : true,
      tinNumber:
        entity.taxations?.length > 0 ? entity.taxations[0].tinNumber : null,
      isUSResidentForAnyOtherReason: entity.isUSResidentForAnyOtherReason,
      isPoliticallyExposed: entity.isPoliticallyExposed,
      isDirector: entity.isDirector,
    };

    const response = await prospectStore.saveCorporateIndividual(payload);
    return response;
  };

  const saveCorporateTransaction = async function (
    currentKyc: any,
    prospectId: string
  ) {
    const payload = {
      prospectId: prospectId,
      averageAmountOfIncomingTransfer:
        currentKyc.averageAmountOfIncomingTransfer,
      averageAmountOfIncomingTransferCurrencyIsoCode:
        currentKyc.averageAmountOfIncomingTransferCurrencyIsoCode,
      averageAmountOfOutgoingTransfer:
        currentKyc.averageAmountOfOutgoingTransfer,
      averageAmountOfOutgoingTransferCurrencyIsoCode:
        currentKyc.averageAmountOfOutgoingTransferCurrencyIsoCode,
      numberOfIncomingTransferPerMonth:
        currentKyc.numberOfIncomingTransferPerMonth,
      numberOfOutgoingTransferPerMonth:
        currentKyc.numberOfOutgoingTransferPerMonth,
    };

    const response = await prospectStore.saveCorporateTransaction(payload);
    return response;
  };

  const saveProspectCounterparty = async function (prospect: any) {
    const response = await prospectStore.saveProspectCounterparty(prospect);
    return response;
  };

  const saveCorporateUnderReview = async function (prospectId: string) {
    const payload = {
      prospectId: prospectId,
    };

    const response = await prospectStore.saveCorporateUnderReview(payload);
    return response;
  };

  // endregion

  const getProspectManager = async function (prospect: any) {
    return await prospectStore.getProspectManager(prospect);
  };

  const getIsLoading = () => {
    return prospectStore.getIsLoading;
  };

  const getIsLoadingAttachment = () => {
    return prospectStore.isLoadingAttachment;
  };

  const getIsLoadingDiscussion = () => {
    return prospectStore.isLoadingDiscussion;
  };

  const deleteCorporateIndividual = async function (
    entity: any,
    prospectId: string
  ) {
    const payload = {
      PersonId: entity.id,
      ProspectId: prospectId,
    };
    await useProspectStore().deleteCorporateIndividual(payload);
  };

  const deleteProspectCounterparty = async function (
    counterpartyId: any,
    prospectId: any
  ) {
    const payload = {
      counterpartyId: counterpartyId,
      prospectId: prospectId,
    };
    await useProspectStore().deleteProspectCounterparty(payload);
  };

  const displayRegistrations = function () {
    useProspectStore().showRegistration();
  };

  const hideRegistrations = function () {
    useProspectStore().hideRegistration();
  };

  const getDisplayRegistrations = function () {
    return useProspectStore().getDisplayRegistrations;
  };

  return {
    getProspect,
    createProspect,
    editProspect,
    getProspectList,
    getProspectType,
    setProspectType,
    addAttachment,
    downloadAttachment,
    getDiscussions,
    getDiscussion,
    addMessage,
    updateMessageStatus,
    getProspectCurrentPage,
    getPageAccessForProspectId,

    // Individual Post-login
    saveIndividualLandingPage,
    saveIndividualRegistration,
    saveIndividualPreKyc,
    SaveIndividualTermsAndConditions,
    saveIndividualToInProgress,
    saveIndividualDocuments,
    downloadOpeningSet,

    // Corporate Post-login
    saveCorporateLandingPage,
    saveCorporateRegistration,
    saveCorporateIndividual,
    deleteCorporateIndividual,
    saveCorporateTransaction,
    saveProspectCounterparty,
    deleteProspectCounterparty,
    saveCorporateUnderReview,

    // Divers
    getProspectManager,
    getIsLoading,
    getIsLoadingAttachment,
    getIsLoadingDiscussion,

    hideRegistrations,
    displayRegistrations,
    getDisplayRegistrations,
  };
};
